"use client";

import {
	AddToCartButtonView,
	deriveAddToCartState
} from "@/composites/cart/AddToCartButton";
import { PriceDisplay } from "@/composites/product/PriceDisplay";
import { type CatalogProduct } from "@/domain/catalog";
import { isPriceVisible } from "@/domain/catalog/online-status";
import { type ButtonProps, Text } from "@pamperedchef/design-system";
import * as styles from "./StickyCartBar.module.scss";

export interface StickyCartBarProps {
	visible: boolean;
	product: CatalogProduct;
	addToCart: {
		onClick: () => void;
		loading: boolean;
	};
	ctaSize?: ButtonProps["size"];
	className?: string;
}

export function StickyCartBar({
	visible,
	product,
	addToCart,
	ctaSize = "lg",
	className
}: StickyCartBarProps) {
	const showPrice = isPriceVisible(product.onlineStatus);
	const display = product.salePrice;
	const list = product.listPrice;
	const isOnSale =
		typeof display === "number" &&
		typeof list === "number" &&
		display < list;

	const state = deriveAddToCartState(product, {
		loading: addToCart.loading
	});

	return (
		<div
			className={[styles["bar"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="StickyCartBar"
			data-visible={visible}
			aria-hidden={!visible}
			inert={!visible}
		>
			<div className={styles["bar__inner"]}>
				<div className={styles["bar__info"]}>
					<Text
						tag="p"
						styledAs="mdr"
						className={styles["bar__title"]}
					>
						{product.title}
					</Text>
					{showPrice ? (
						<PriceDisplay
							display={display}
							strikeThrough={isOnSale ? list : undefined}
							size="compact"
							className={styles["bar__price"]}
						/>
					) : null}
				</div>
				<AddToCartButtonView
					state={state}
					onClick={addToCart.onClick}
					size={ctaSize}
					className={styles["bar__cta"]}
				/>
			</div>
		</div>
	);
}
